import { useEffect, useState } from 'react';
import { PropertyCardFragment } from '../../gatsby-graphql';
import {
  IPropertiesFiltersContext,
  ListingOrder,
} from '../context/PropertiesFiltersContext';
import useHandleScroll from './useHandleScroll';

const usePropertiesFilters = (
  properties: PropertyCardFragment[]
): IPropertiesFiltersContext => {
  const listingOrderList: { value: ListingOrder; label: ListingOrder }[] = [
    {
      value: 'Recientes',
      label: 'Recientes',
    },
    {
      value: 'Antiguos',
      label: 'Antiguos',
    },
    {
      value: 'Baratos',
      label: 'Baratos',
    },
    {
      value: 'Caros',
      label: 'Caros',
    },
  ];
  const { disableWindowScroll, enableWindowScroll } = useHandleScroll();
  const prices = properties.map((property) => Number(property.price));
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  const priceDeltas: [number, number] = [
    minPrice === maxPrice ? 0 : minPrice,
    maxPrice,
  ];
  const [priceValues, setPriceValues] = useState<[number, number]>(priceDeltas);
  const [bedroomCount, setBedroomCount] = useState<number>(0);
  const [bathroomCount, setBathroomCount] = useState<number>(0);
  const [garagesCount, setGaragesCount] = useState<number>(0);
  const [checkedPropertySpecs, setCheckedPropertySpecs] = useState<string[]>(
    []
  );
  const [filterProperties, setFilterProperties] = useState<
    PropertyCardFragment[]
  >(properties);
  const [listingType, setListingType] = useState<'grid' | 'list'>('grid');
  const [listingOrder, setListingOrder] = useState<ListingOrder>('Recientes');
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [isTouched, setIsTouched] = useState<boolean>(false);

  const checkRoomCount = (
    property: PropertyCardFragment,
    room: 'bedroomsCount' | 'bathroomsCount' | 'garagesCount',
    activeCount: number
  ) =>
    activeCount
      ? activeCount === 5
        ? Number(property[room]) >= activeCount
        : Number(property[room]) === activeCount
      : true;

  const checkPrice = (
    property: PropertyCardFragment,
    prices: [number, number]
  ) =>
    Number(property?.price) >= prices[0] &&
    Number(property?.price) <= prices[1];

  const checkSpecs = (property: PropertyCardFragment, checkedSpecs: string[]) =>
    checkedSpecs.every((checkedSpec) =>
      property.propertySpecs?.find((spec) => spec?.name === checkedSpec)
    );

  const sortProperties = (
    propertyA: PropertyCardFragment,
    propertyB: PropertyCardFragment
  ) => {
    if (listingOrder === 'Caros') {
      return Number(propertyA?.price) > Number(propertyB?.price) ? -1 : 1;
    } else if (listingOrder === 'Baratos') {
      return Number(propertyA?.price) > Number(propertyB?.price) ? 1 : -1;
    } else if (listingOrder === 'Recientes') {
      return new Date(propertyA?.createdAt) > new Date(propertyB?.createdAt)
        ? -1
        : 1;
    } else if (listingOrder === 'Antiguos') {
      return new Date(propertyA?.createdAt) > new Date(propertyB?.createdAt)
        ? 1
        : -1;
    }
    return 0;
  };

  useEffect(() => {
    if (!isTouched) setIsTouched(true);
    setFilterProperties(() =>
      properties
        .filter((property) => {
          const price = checkPrice(property, priceValues);
          const bedrooms = checkRoomCount(
            property,
            'bedroomsCount',
            bedroomCount
          );
          const bathrooms = checkRoomCount(
            property,
            'bathroomsCount',
            bathroomCount
          );
          const garages = checkRoomCount(
            property,
            'garagesCount',
            garagesCount
          );
          const specs = checkSpecs(property, checkedPropertySpecs);
          return price && bedrooms && bathrooms && garages && specs;
        })
        .sort(sortProperties)
    );
  }, [
    bedroomCount,
    bathroomCount,
    garagesCount,
    priceValues,
    checkedPropertySpecs,
    listingOrder,
  ]);

  useEffect(() => {
    if (isDrawerOpen) {
      disableWindowScroll();
    } else {
      enableWindowScroll();
    }
  }, [isDrawerOpen]);

  const handlePropertySpecChange = (checked: boolean, name: string) => {
    if (checked) {
      setCheckedPropertySpecs([...checkedPropertySpecs, name]);
    } else {
      setCheckedPropertySpecs(
        checkedPropertySpecs.filter((propertySpec) => propertySpec !== name)
      );
    }
  };

  return {
    priceDeltas,
    priceValues,
    setPriceValues,
    bedroomCount,
    setBathroomCount,
    bathroomCount,
    setBedroomCount,
    garagesCount,
    setGaragesCount,
    checkedPropertySpecs,
    handlePropertySpecChange,
    filterProperties,
    filterPropertiesLength: filterProperties.length,
    listingType,
    setListingType,
    setListingOrder,
    listingOrderList,
    isDrawerOpen,
    setIsDrawerOpen,
    isTouched,
  };
};

export default usePropertiesFilters;
